import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import {
  LayoutDashboard, FileText, ShoppingCart, Truck, Users, Building2, Package, Boxes,
  FolderKanban, LogOut, Menu, X, LineChart, Scale, Settings as SettingsIcon
} from "lucide-react";
import { useState } from "react";
import { Toaster } from "@/components/ui/sonner";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { section: "Akuntansi" },
  { to: "/sales", label: "Invoice Penjualan", icon: FileText, id: "nav-sales" },
  { to: "/purchase", label: "PO / Pembelian", icon: ShoppingCart, id: "nav-purchase" },
  { to: "/delivery", label: "Surat Jalan", icon: Truck, id: "nav-delivery" },
  { section: "Master Data" },
  { to: "/clients", label: "Klien", icon: Users, id: "nav-clients" },
  { to: "/suppliers", label: "Supplier", icon: Building2, id: "nav-suppliers" },
  { to: "/products", label: "Produk & Stok", icon: Package, id: "nav-products" },
  { to: "/assets", label: "Aset", icon: Boxes, id: "nav-assets" },
  { section: "Project" },
  { to: "/projects", label: "Manajemen Project", icon: FolderKanban, id: "nav-projects" },
  { section: "Laporan" },
  { to: "/reports", label: "Laba Rugi", icon: LineChart, id: "nav-reports" },
  { to: "/balance-sheet", label: "Neraca", icon: Scale, id: "nav-balance" },
  { section: "Pengaturan" },
  { to: "/settings", label: "Branding", icon: SettingsIcon, id: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-[#FDFDFB]">
      <aside style={{ backgroundColor: brand.sidebar_color }} className={`fixed lg:sticky top-0 h-screen w-64 text-[#FDFDFB] z-40 transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform duration-200 flex flex-col`}>
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={brand.brand_name} className="w-8 h-8 object-contain bg-white/10 rounded-sm"/>
            ) : (
              <div style={{ backgroundColor: brand.accent_color }} className="w-8 h-8 flex items-center justify-center font-display font-bold text-[#151A18]">
                {(brand.brand_name || "L").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display text-lg leading-none truncate">{brand.brand_name}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-1 truncate">{brand.tagline}</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {nav.map((n, i) => n.section ? (
            <div key={i} className="px-3 pt-5 pb-2 text-[10px] tracking-[0.2em] uppercase text-white/40">{n.section}</div>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.id}
              style={({ isActive }) => isActive ? { borderLeftColor: brand.accent_color, borderLeftWidth: "2px", borderLeftStyle: "solid" } : {}}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
              <n.icon size={18} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/60 mb-2 truncate" data-testid="user-email">{user?.email}</div>
          <button onClick={handleLogout} data-testid="btn-logout"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 transition-colors">
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </aside>

      <button onClick={() => setOpen(!open)} data-testid="btn-menu"
        style={{ backgroundColor: brand.sidebar_color }}
        className="lg:hidden fixed top-4 left-4 z-50 text-white p-2 rounded-sm">
        {open ? <X size={18} /> : <Menu size={18} />}
      </button>

      <main className="flex-1 min-w-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
          <Outlet />
        </div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
